import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canViewFinancial, formatINR } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, FileText, FileSpreadsheet, TrendingDown as TrendingDownIcon, Loader2 } from "lucide-react";
import DowntimeReport from "@/components/DowntimeReport";
import BreakdownPareto from "@/components/BreakdownPareto";
import ThreeWayMatchReport from "@/components/ThreeWayMatchReport";
import LandedCostReconciliation from "@/components/LandedCostReconciliation";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#7C1D3A", "#B8355A", "#E07897", "#F5C4D3", "#D97706", "#2563EB"];

export default function Reports() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const showFinancial = canViewFinancial(user?.role);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    })();
  }, []);

  if (!stats) return <div className="text-slate-500">Loading reports...</div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Analytics</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Reports & Insights</h1>
        <p className="text-slate-500 mt-1 text-sm">Portfolio-wide analytics across categories, status and depreciation</p>
      </div>

      <StatutoryReports showFinancial={showFinancial} />

      {showFinancial && <LandedCostReconciliation />}

      {showFinancial ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard label="Total Purchase Value" value={formatINR(stats.total_purchase_value)} />
            <StatCard label="Current Book Value" value={formatINR(stats.total_current_value)} highlight />
            <StatCard label="Accumulated Depreciation" value={formatINR(stats.total_depreciation)} />
          </div>

          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Value by Category</CardTitle></CardHeader>
            <CardContent className="p-6">
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={stats.category_values || []}>
                  <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Legend />
                  <Bar dataKey="purchase" fill="#B8355A" name="Purchase Value" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="current" fill="#7C1D3A" name="Current Value" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            <Lock className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            Financial reports are restricted to Admin, Manager, and Accounts roles.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Assets by Category</CardTitle></CardHeader>
          <CardContent className="p-6">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={stats.by_category}
                  cx="50%" cy="50%" outerRadius={100} dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {stats.by_category.map((entry, i) => (
                    <Cell key={entry.name || `cat-${i}`} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Maintenance Trends (Last 6 Months)</CardTitle></CardHeader>
          <CardContent className="p-6">
            {stats.trends && stats.trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stats.trends}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="preventive" stackId="a" fill="#7C1D3A" name="Preventive" />
                  <Bar dataKey="breakdown" stackId="a" fill="#D97706" name="Breakdown" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-16 text-slate-400 text-sm">No maintenance logged yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Downtime & OEE panel */}
      <DowntimeReport />

      {/* Breakdown Pareto */}
      <BreakdownPareto />

      {/* 3-Way Match — visible for financial roles only (endpoint enforces RBAC too) */}
      {showFinancial && <ThreeWayMatchReport />}
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`p-6 rounded-lg border ${highlight ? "bg-[#fcf0f3] border-[#f5c4d3]" : "bg-white border-slate-200"}`}>
      <div className="text-xs font-semibold tracking-wider uppercase text-slate-500">{label}</div>
      <div className={`mt-3 font-heading text-3xl font-bold ${highlight ? "text-[#7C1D3A]" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function StatutoryReports({ showFinancial }) {
  const [busy, setBusy] = useState("");
  const [fy, setFy] = useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const s = d.getMonth() >= 3 ? y : y - 1;
    return `${String(s).slice(2)}-${String(s + 1).slice(2)}`;
  });

  const download = async (path, filename, kind) => {
    setBusy(kind);
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Failed (${res.status})`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally { setBusy(""); }
  };

  return (
    <Card data-testid="statutory-reports-card">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="font-heading text-base">Statutory & Downloadable Reports</CardTitle>
      </CardHeader>
      <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {showFinancial && (
          <div className="border border-slate-200 rounded-lg p-4 flex flex-col">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#fcf0f3] flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#7C1D3A]" />
              </div>
              <div>
                <div className="font-heading font-semibold text-slate-900">Fixed Asset Register</div>
                <div className="text-xs text-slate-500">Companies Act, Schedule II style</div>
              </div>
            </div>
            <p className="text-xs text-slate-600 mb-3 flex-1">Gross Block · Accumulated Depreciation · Net Block per asset, with company totals.</p>
            <Button
              onClick={() => download("/reports/far.pdf", `FAR_${new Date().toISOString().slice(0, 10)}.pdf`, "far")}
              disabled={busy === "far"}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36] w-full"
              data-testid="download-far-btn"
            >
              {busy === "far" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Download PDF
            </Button>
          </div>
        )}

        {showFinancial && (
          <div className="border border-slate-200 rounded-lg p-4 flex flex-col">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#fcf0f3] flex items-center justify-center">
                <TrendingDownIcon className="w-5 h-5 text-[#7C1D3A]" />
              </div>
              <div>
                <div className="font-heading font-semibold text-slate-900">Depreciation Schedule</div>
                <div className="text-xs text-slate-500">Per-FY WDV / SLM breakdown</div>
              </div>
            </div>
            <div className="mb-3">
              <label className="text-xs text-slate-500">Financial Year</label>
              <select
                value={fy}
                onChange={(e) => setFy(e.target.value)}
                className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                data-testid="depr-fy-select"
              >
                {["23-24", "24-25", "25-26", "26-27", "27-28"].map((f) => <option key={f} value={f}>FY {f}</option>)}
              </select>
            </div>
            <Button
              onClick={() => download(`/reports/depreciation-schedule.pdf?fy=${fy}`, `DepreciationSchedule_FY${fy}.pdf`, "depr")}
              disabled={busy === "depr"}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36] w-full"
              data-testid="download-depr-btn"
            >
              {busy === "depr" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingDownIcon className="w-4 h-4 mr-2" />}
              Download PDF
            </Button>
          </div>
        )}

        <div className="border border-slate-200 rounded-lg p-4 flex flex-col">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <div className="font-heading font-semibold text-slate-900">Full Asset List</div>
              <div className="text-xs text-slate-500">Excel (.xlsx) export</div>
            </div>
          </div>
          <p className="text-xs text-slate-600 mb-3 flex-1">
            {showFinancial ? "All fields including financials & depreciation." : "Non-financial fields only (based on your role)."}
          </p>
          <Button
            onClick={() => download("/reports/assets.xlsx", `Assets_${new Date().toISOString().slice(0, 10)}.xlsx`, "xlsx")}
            disabled={busy === "xlsx"}
            variant="outline"
            className="w-full"
            data-testid="download-xlsx-btn"
          >
            {busy === "xlsx" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Download Excel
          </Button>
        </div>

        {showFinancial && (
          <div className="border border-slate-200 rounded-lg p-4 flex flex-col">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-purple-700" />
              </div>
              <div>
                <div className="font-heading font-semibold text-slate-900">GST Input Register</div>
                <div className="text-xs text-slate-500">Input credit — spares & consumables POs</div>
              </div>
            </div>
            <div className="mb-3">
              <label className="text-xs text-slate-500">Filter by Financial Year</label>
              <select
                value={fy}
                onChange={(e) => setFy(e.target.value)}
                className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                data-testid="gst-fy-select"
              >
                <option value="">All FYs</option>
                {["23-24", "24-25", "25-26", "26-27", "27-28"].map((f) => <option key={f} value={f}>FY {f}</option>)}
              </select>
            </div>
            <p className="text-[11px] text-slate-500 mb-3 flex-1">
              Entries auto-posted when a revenue PO is "Posted to Books". Capital-goods GST is capitalised into the asset value and does not appear here.
            </p>
            <Button
              onClick={() => download(`/reports/gst-register.xlsx${fy ? `?fy=${fy}` : ""}`, `GST_Register_${fy || "All"}.xlsx`, "gst")}
              disabled={busy === "gst"}
              className="bg-purple-700 hover:bg-purple-800 w-full"
              data-testid="download-gst-register-btn"
            >
              {busy === "gst" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Download GST Register (.xlsx)
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
